import { useEffect, useMemo, useRef, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { MessageCircle } from 'lucide-react'
import { usePropertyThreadsInfinite, useThreadsInfinite } from '@/hooks/useApi'
import { getChannelLabel, getChannelBadgeVariant, formatDateRange } from '@/lib/format-utils'
import { Avatar } from '@/components/ui/avatar'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { ChannelMark } from '@/components/channel-icons'
import { Button } from '@/components/ui/button'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { cn } from '@/lib/utils'

/**
 * Shape of a single row returned by GET /admin/properties/:id/threads/page
 * and GET /admin/threads/page - only the fields this component reads.
 */
interface ThreadListItem {
  threadId: string
  channel?: string
  guest?: { name?: string; email?: string } | null
  lastMessageAt?: string | null
  lastMessagePreview?: string | null
  unreadCount?: number
  needsReply?: boolean
  checkIn?: string | null
  checkOut?: string | null
  property?: { displayName?: string } | null
}

interface ConversationListProps {
  propertyId?: string
  selectedThreadId?: string | null
  onSelectThread?: (item: ThreadListItem) => void
  limit?: number
}

const FILTERS = [
  { key: 'all', label: 'All', needsReply: false },
  { key: 'needs-reply', label: 'Needs reply', needsReply: true },
]

function formatRelativeTime(value?: string | Date | null) {
  if (!value) return null
  try {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return null
  }
}

/**
 * Standalone conversation-list pane for the redesigned property Messages
 * inbox (docs/archive/messages-redesign-plan.md). Infinite-scrolls through
 * GET /admin/properties/:id/threads/page via usePropertyThreadsInfinite,
 * with "All" / "Needs reply" filter chips that pass `needsReply` through
 * to the server (the server filters before pagination - this component
 * never filters already-loaded items on the client).
 *
 * Not yet wired into property-details.jsx - see Wave 2 step 5 in
 * docs/archive/messages-redesign-pr-slices.md, which depends on the thread-pane
 * and composer slices landing first.
 *
 * Only one booker name is ever shown per conversation - no fabricated
 * co-traveler names or avatars (data-honesty constraint, see
 * docs/archive/messages-redesign-plan.md).
 *
 * Omit `propertyId` to list across ALL properties instead (the admin
 * portal-wide Inbox, src/pages/inbox.jsx) via GET /admin/threads/page -
 * each row then also shows which property the conversation belongs to,
 * since a single-property list no longer applies. Both underlying hooks
 * are always called (each self-disables via its own `enabled` check) so
 * which one is active is a plain runtime choice, not a conditional hook
 * call.
 */
export function ConversationList({
  propertyId,
  selectedThreadId,
  onSelectThread,
  limit,
}: ConversationListProps) {
  const [filterKey, setFilterKey] = useState('all')
  const activeFilter = FILTERS.find(f => f.key === filterKey) ?? FILTERS[0]
  const sentinelRef = useRef<HTMLLIElement>(null)

  const propertyScopedQuery = usePropertyThreadsInfinite({
    propertyId,
    limit,
    needsReply: activeFilter.needsReply,
  })
  const globalQuery = useThreadsInfinite({
    limit,
    needsReply: activeFilter.needsReply,
    enabled: !propertyId,
  })
  const query = propertyId ? propertyScopedQuery : globalQuery
  const {
    data,
    isLoading,
    error,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = query

  const items: ThreadListItem[] = useMemo(() => {
    const pages = (data?.pages ?? []) as Array<{ items: ThreadListItem[] }>
    return pages.flatMap(page => page.items)
  }, [data])

  // Infinite scroll: fetch the next page once the sentinel row at the
  // bottom of the list enters the viewport.
  //
  // Stop auto-fetching once a next-page request has failed
  // (isFetchNextPageError): the sentinel stays visible while the inline
  // Retry control is shown, and isFetchingNextPage flips back to false
  // after the failure, so without this guard a new observer would fire
  // immediately and call fetchNextPage() again in an unbounded
  // request/retry loop (Codex pre-review on this PR). Once a page fails,
  // retrying is left to the explicit Retry button only.
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage || isFetchNextPageError) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border p-3">
        {FILTERS.map(filter => (
          <Button
            key={filter.key}
            type="button"
            size="sm"
            variant={filter.key === filterKey ? 'default' : 'secondary'}
            onClick={() => setFilterKey(filter.key)}
            aria-pressed={filter.key === filterKey}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-3 p-3">
            <LoadingSkeleton className="h-16 w-full" />
            <LoadingSkeleton className="h-16 w-full" />
            <LoadingSkeleton className="h-16 w-full" />
          </div>
        ) : error && items.length === 0 ? (
          // Only replace the whole pane with the full error state on an
          // initial-load failure. A failed *next*-page fetch (below) keeps
          // the already-loaded rows on screen with its own inline retry -
          // React Query retains data.pages on a fetchNextPage failure, so
          // there is no reason to throw away conversations the user can
          // already see (Codex pre-review on this PR).
          <ErrorState error={error} onRetry={refetch} />
        ) : items.length === 0 ? (
          <EmptyState
            message={
              activeFilter.needsReply
                ? 'No conversations need a reply right now.'
                : 'No conversations yet.'
            }
          />
        ) : (
          <ul role="list">
            {items.map(item => (
              <li key={item.threadId}>
                <button
                  type="button"
                  onClick={() => onSelectThread?.(item)}
                  aria-current={item.threadId === selectedThreadId ? 'true' : undefined}
                  className={cn(
                    'flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-surface2',
                    item.threadId === selectedThreadId && 'bg-surface2'
                  )}
                >
                  <Avatar name={item.guest?.name} seed={item.threadId} className="mt-0.5" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium text-text">
                          {item.guest?.name || 'Guest'}
                        </span>
                        {item.needsReply && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full bg-mint"
                            title="Needs reply"
                            aria-label="Needs reply"
                          />
                        )}
                      </span>
                      <span className="shrink-0 text-xs text-textMuted">
                        {formatRelativeTime(item.lastMessageAt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-sm',
                          (item.unreadCount ?? 0) > 0 ? 'font-medium text-text' : 'text-textMuted'
                        )}
                      >
                        {item.lastMessagePreview || 'No messages yet'}
                      </span>
                      {item.channel && (
                        <Badge
                          variant={getChannelBadgeVariant(item.channel) as BadgeProps['variant']}
                          className="shrink-0 gap-1 text-xs"
                        >
                          <ChannelMark channel={item.channel} className="h-3.5 w-3.5" />
                          {getChannelLabel(item.channel)}
                        </Badge>
                      )}
                    </div>
                    {!propertyId && item.property?.displayName && (
                      <div className="truncate text-xs text-textFaint">
                        {item.property.displayName}
                      </div>
                    )}
                    {item.checkIn && item.checkOut && (
                      <div className="text-xs text-textFaint">
                        {formatDateRange(item.checkIn, item.checkOut)}
                      </div>
                    )}
                  </div>
                </button>
              </li>
            ))}
            <li ref={sentinelRef} />
            {isFetchingNextPage ? (
              <li className="flex items-center justify-center gap-2 py-4 text-sm text-textMuted">
                <MessageCircle className="h-4 w-4 animate-pulse" />
                Loading more…
              </li>
            ) : isFetchNextPageError ? (
              <li className="flex flex-col items-center gap-2 py-4 text-center text-sm">
                <span className="text-textMuted">Couldn&apos;t load more conversations.</span>
                <Button type="button" variant="outline" size="sm" onClick={() => fetchNextPage()}>
                  Retry
                </Button>
              </li>
            ) : hasNextPage ? (
              // Visible fallback for the scroll-triggered fetch above - keeps
              // pagination usable in test environments (jsdom has no real
              // IntersectionObserver firing) and for keyboard/reduced-motion
              // users who never trigger the scroll intersection.
              <li className="py-2 text-center">
                <Button type="button" variant="ghost" size="sm" onClick={() => fetchNextPage()}>
                  Load more
                </Button>
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </div>
  )
}
